import { useEffect, useState } from "react";
import { getBillingDashboard, type BillingDashboard } from "../../obras/obrasApi";
import { FinancialKpiCard, formatCurrency, formatPercent } from "../Obras/components/FinancialDashboardShared";

const TOP_CLIENTS = 5;

function computeDelta(current: number, previous: number): number | null {
  if (!Number.isFinite(previous) || previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

function deltaHint(current: number, previous: number): string {
  const delta = computeDelta(current, previous);
  if (delta === null) return "Sem base comparável";
  const sign = delta >= 0 ? "+" : "";
  return `${sign}${formatPercent(delta)} vs período anterior`;
}

export function BillingDashboardPanel() {
  const [dashboard, setDashboard] = useState<BillingDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getBillingDashboard(TOP_CLIENTS)
      .then(setDashboard)
      .catch((err) => setError(err instanceof Error ? err.message : "Erro ao carregar a faturação."));
  }, []);

  if (error) {
    return <div className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3">{error}</div>;
  }
  if (!dashboard) {
    return <div className="text-secondary-text py-6">A carregar faturação…</div>;
  }

  const maxClientTotal = dashboard.topClients.reduce((max, client) => Math.max(max, client.total), 0);

  return (
    <section className="space-y-5">
      <div>
        <h2 className="text-foreground" style={{ fontSize: "var(--text-h3)", fontWeight: "var(--font-weight-semi-bold)" }}>
          Faturação
        </h2>
        <p className="text-secondary-text mt-1">
          Faturação emitida a clientes: mês corrente, comparativo homólogo, ano corrente vs anterior e por cliente.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <FinancialKpiCard label="Mês atual" value={formatCurrency(dashboard.currentMonthTotal)} />
        <FinancialKpiCard
          label="Homólogo (mês ano anterior)"
          value={formatCurrency(dashboard.sameMonthLastYearTotal)}
          hint={deltaHint(dashboard.currentMonthTotal, dashboard.sameMonthLastYearTotal)}
        />
        <FinancialKpiCard label="YTD ano atual" value={formatCurrency(dashboard.currentYearToDateTotal)} />
        <FinancialKpiCard
          label="YTD ano anterior"
          value={formatCurrency(dashboard.priorYearToDateTotal)}
          hint={deltaHint(dashboard.currentYearToDateTotal, dashboard.priorYearToDateTotal)}
        />
      </div>

      <div className="bg-card border border-border" style={{ borderRadius: "var(--radius-card)" }}>
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-foreground" style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
            Top {TOP_CLIENTS} clientes por faturação
          </h3>
        </div>
        <div className="p-5">
          {dashboard.topClients.length === 0 ? (
            <div className="text-secondary-text py-2">Sem faturação registada.</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="px-2 py-2">Cliente</th>
                  <th className="px-2 py-2 text-right">Faturação</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.topClients.map((client) => (
                  <tr key={client.clientName} className="border-b border-border last:border-b-0">
                    <td className="px-2 py-2">
                      <div className="text-foreground">{client.clientName}</div>
                      <div className="mt-1 h-1.5 bg-muted" style={{ borderRadius: "var(--radius)" }}>
                        <div
                          className="h-1.5 bg-primary"
                          style={{
                            width: maxClientTotal > 0 ? `${(client.total / maxClientTotal) * 100}%` : "0%",
                            borderRadius: "var(--radius)",
                          }}
                        />
                      </div>
                    </td>
                    <td className="px-2 py-2 text-right align-top text-foreground">{formatCurrency(client.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </section>
  );
}
